import {
    Box,
    Button,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import CustomDropdown from '../Dropdown';
import moment from 'moment';
import { HUB_TYPE_ENUM } from '../../utils/constants';
import { OnlineStateIcon, RightArrowIcon } from '../../assets/svg';
import { sortTime } from '../Modals/AssignStaffToHubModal';
import Modal from '../Modals/Modal';
import AddShiftTimingModal from '../Modals/AddShiftTimingModal';
import {
    deleteShiftTiming,
    deleteShiftTimingBD,
} from '../../store/actions/settingsActionCreator';
import { isBlueDartOrg } from '../../utils/appHelper';

// Types
interface ShiftLineItem {
    id: string;
    name?: string;
    shortName?: string;
    startTimeEpochMillis: number;
    endTimeEpochMillis: number;
    primaryOperation?: string;
    slotShift?: boolean;
    staffCount?: number;
    rateCardIdentifier?: string;
}

interface HubShiftMapping {
    hubId?: string;
    hubName?: string;
    primaryOperation?: string;
    shiftLineItems: ShiftLineItem[];
}

interface ShiftData {
    totalHubCount?: number;
    totalShiftCount?: number;
    hubShiftMappings?: HubShiftMapping[];
    presentShiftIds?: string[];
}

interface HubInfo {
    id: string;
    name: string;
    primaryOperation?: string;
    bluedartUniqueStoreId?: string;
}

interface Permissions {
    create?: boolean;
    update?: boolean;
    delete?: boolean;
    read?: boolean;
}

interface ShiftColumnItemPopperV2Props {
    limit?: number;
    inline?: boolean;
    data?: ShiftData;
    hubInfo?: HubInfo;
    permissions?: Permissions;
    onEditSuccess?: () => void;
    onDeleteSuccess?: () => void;
    onAddSuccess?: () => void;
}

const useStyles = makeStyles((theme) => ({
    actionIcon: {
        padding: 2,
        marginLeft: 2,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    editIcon: {
        fontSize: 12,
        color: 'inherit',
    },
    deleteIcon: {
        fontSize: 12,
        color: 'inherit',
    },
    addButton: {
        border: '1px dashed #43419e',
        color: '#43419e',
        padding: '0 4px',
        borderRadius: '4px',
        marginRight: '4px',
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        minHeight: 24,
        '&:hover': {
            backgroundColor: 'rgba(67, 65, 158, 0.1)',
        },
    },
    saveButton: {
        backgroundColor: theme.palette.error.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
}));

const LimitTag: React.FC<{
    children: React.ReactNode;
    style?: React.CSSProperties;
}> = ({ children, ...props }) => (
    <Box
        style={{
            border: '1px solid',
            backgroundColor: '#14112d',
            color: '#fff',
            padding: '0 8px',
            borderRadius: '8px',
            marginRight: '4px',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
        }}
        {...props}
    >
        {children}
    </Box>
);

export const checkCurrentAvailableShift = (value: ShiftData): ShiftData => {
    const data = { ...value };
    const currentTime = moment();
    if (!data?.hubShiftMappings?.length) return { ...data };
    const currentShift = data?.hubShiftMappings?.find((hub) => {
        return hub.shiftLineItems.find((shift) => {
            return moment(currentTime).isBetween(
                shift.startTimeEpochMillis,
                shift.endTimeEpochMillis,
                'minute',
            );
        });
    });

    if (currentShift) {
        data.totalHubCount = 1;
        data.totalShiftCount = 1;
        data.hubShiftMappings = [currentShift];
    } else {
        data.totalHubCount = 0;
        data.totalShiftCount = 0;
        data.hubShiftMappings = [];
    }
    return { ...data };
};

const ShiftColumnItemPopperV2: React.FC<ShiftColumnItemPopperV2Props> = ({
    limit = 1,
    inline = false,
    data = {},
    hubInfo,
    permissions = {},
    onEditSuccess,
    onDeleteSuccess,
    onAddSuccess,
    ...props
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isBlueDart = !!isBlueDartOrg?.();

    const {
        totalHubCount = 0,
        totalShiftCount = 0,
        hubShiftMappings = [],
        presentShiftIds = [],
    } = data ?? {};

    // State for modals
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedShift, setSelectedShift] = useState<ShiftLineItem | null>(
        null,
    );
    const [deleteLoading, setDeleteLoading] = useState(false);

    let rowItem: any[] = [];
    if (!hubShiftMappings?.length) {
        rowItem = [
            {
                item: 'No Data',
                value: '',
            },
        ];
    } else {
        rowItem = [...hubShiftMappings];
    }

    const isDisabled = !totalHubCount && !totalShiftCount;
    let shiftList: ShiftLineItem[] = [];
    if (hubShiftMappings?.length) {
        hubShiftMappings?.forEach((hub) => {
            if (hub?.shiftLineItems?.length) {
                shiftList = sortTime([...shiftList, ...hub?.shiftLineItems]);
            }
        });
    }
    const isShiftGreaterThanLimit = shiftList?.length > limit;
    const tagLength = isShiftGreaterThanLimit
        ? limit ?? shiftList?.length
        : shiftList?.length;

    // Handlers
    const handleEditClick = (e: React.MouseEvent, shift: ShiftLineItem) => {
        e?.stopPropagation();
        e?.preventDefault();
        setSelectedShift(shift);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (e: React.MouseEvent, shift: ShiftLineItem) => {
        e?.stopPropagation();
        e?.preventDefault();
        setSelectedShift(shift);
        setIsDeleteModalOpen(true);
    };

    const handleAddClick = (e: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();
        setSelectedShift(null);
        setIsAddModalOpen(true);
    };

    const handleDeleteClose = () => {
        setIsDeleteModalOpen(false);
        setSelectedShift(null);
    };

    const handleEditClose = () => {
        setIsEditModalOpen(false);
        setSelectedShift(null);
        onEditSuccess?.();
    };

    const handleAddClose = () => {
        setIsAddModalOpen(false);
        setSelectedShift(null);
        onAddSuccess?.();
    };

    const handleDeleteConfirm = () => {
        if (!selectedShift?.id) return;
        const deleteAction = isBlueDart
            ? deleteShiftTimingBD
            : deleteShiftTiming;
        setDeleteLoading(true);
        dispatch(
            deleteAction?.(
                selectedShift.id,
                (resp: any) => {
                    setDeleteLoading(false);
                    handleDeleteClose();
                    onDeleteSuccess?.();
                },
                (err: any) => {
                    console.error('Delete shift error:', err);
                    setDeleteLoading(false);
                    handleDeleteClose();
                },
            ),
        );
    };

    const deleteActions = [
        <Button
            key={1}
            variant="contained"
            color="default"
            className="cancelButton"
            onClick={handleDeleteClose}
            disableElevation
        >
            No
        </Button>,
        <Button
            key={2}
            variant="contained"
            color="default"
            className={classes.saveButton}
            onClick={handleDeleteConfirm}
            disableElevation
            disabled={deleteLoading}
        >
            Yes
        </Button>,
    ];

    const renderShiftChip = (shift: ShiftLineItem, index: number) => {
        const currentTime = moment();
        let color = '#f44336';
        color = shift?.primaryOperation
            ? (HUB_TYPE_ENUM as any)[
                  shift?.primaryOperation?.toUpperCase() ?? ''
              ]?.color
            : '#f44336';

        const isShiftActive = moment(currentTime).isBetween(
            shift?.startTimeEpochMillis,
            shift?.endTimeEpochMillis,
            'minute',
        );

        const isPresentShiftActive = presentShiftIds?.includes(shift?.id);

        return (
            <Box
                key={`shift-${shift.id}-${index}`}
                style={{
                    border: '1px solid',
                    borderColor: color,
                    padding: '0 4px',
                    paddingLeft: 8,
                    borderRadius: '4px',
                    marginRight: '4px',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    ...(isPresentShiftActive
                        ? {
                              color: '#fff',
                              backgroundColor: color,
                          }
                        : { opacity: 0.6 }),
                }}
            >
                {isShiftActive && (
                    <OnlineStateIcon
                        style={{
                            marginRight: '2px',
                        }}
                        fill={isPresentShiftActive ? '#fff' : '#4CAF50'}
                    />
                )}
                {shift?.startTimeEpochMillis && shift?.endTimeEpochMillis
                    ? `${moment(shift?.startTimeEpochMillis).format(
                          'HH:mm',
                      )} - ${moment(shift?.endTimeEpochMillis).format('HH:mm')}`
                    : ''}{' '}
                {/* Edit Icon */}
                {permissions?.update && (
                    <IconButton
                        className={classes.actionIcon}
                        onClick={(e) => handleEditClick(e, shift)}
                        size="small"
                        disabled={shift?.slotShift}
                    >
                        <EditIcon
                            className={classes.editIcon}
                            style={{
                                color: isPresentShiftActive ? '#fff' : color,
                            }}
                        />
                    </IconButton>
                )}
                {/* Delete Icon */}
                {permissions?.delete && (
                    <IconButton
                        className={classes.actionIcon}
                        onClick={(e) => handleDeleteClick(e, shift)}
                        size="small"
                    >
                        <DeleteIcon
                            className={classes.deleteIcon}
                            style={{
                                color: isPresentShiftActive
                                    ? '#fff'
                                    : '#f44336',
                            }}
                        />
                    </IconButton>
                )}
                <RightArrowIcon
                    fill={isPresentShiftActive ? '#fff' : color}
                    style={{
                        marginLeft: '2px',
                    }}
                />
            </Box>
        );
    };

    return (
        <>
            <CustomDropdown
                buttonProps={{
                    disabled: isDisabled && !permissions?.create,
                }}
                title={{
                    render: (
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                            }}
                        >
                            {inline ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        fontSize: 16,
                                    }}
                                >
                                    {totalHubCount} Hub {totalShiftCount} shifts
                                    {isShiftGreaterThanLimit ? (
                                        <LimitTag>
                                            +{totalShiftCount - 2}
                                        </LimitTag>
                                    ) : (
                                        <ExpandMoreIcon
                                            fontSize="small"
                                            style={{
                                                marginLeft: '4px',
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        fontSize: 12,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {totalShiftCount ? (
                                        Array(tagLength)
                                            .fill(tagLength)
                                            .map((_, index) => {
                                                const shift = shiftList[index];
                                                return renderShiftChip(
                                                    shift,
                                                    index,
                                                );
                                            })
                                    ) : (
                                        <Box
                                            style={{
                                                border: '1px solid #c2c2c2',
                                                padding: '0 8px',
                                                borderRadius: '4px',
                                                marginRight: '8px',
                                                marginBottom: '4px',
                                                color: '#c2c2c2',
                                            }}
                                        >
                                            No Shift Assigned
                                        </Box>
                                    )}
                                    {totalShiftCount > 1 &&
                                        isShiftGreaterThanLimit && (
                                            <LimitTag>
                                                +{totalShiftCount - limit}
                                            </LimitTag>
                                        )}
                                    {/* Add Shift Button */}
                                    {permissions?.create && (
                                        <Box
                                            className={classes.addButton}
                                            onClick={handleAddClick}
                                        >
                                            <AddIcon style={{ fontSize: 16 }} />
                                        </Box>
                                    )}
                                </div>
                            )}
                        </Box>
                    ),
                }}
                disabled={true}
                buttonStyles={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: 14,
                    textAlign: 'left',
                }}
                options={rowItem?.map((hub) =>
                    hub?.item
                        ? { ...hub }
                        : {
                              style: {
                                  minHeight: '100px',
                                  padding: 0,
                                  minWidth: '300px',
                              },
                              render: () => {
                                  return (
                                      <Box
                                          style={{
                                              borderRadius: 8,
                                              backgroundColor: '#fff',
                                              width: '150%',
                                              padding: 8,
                                              paddingBottom: 4,
                                          }}
                                      >
                                          <Box
                                              style={{
                                                  backgroundColor: (
                                                      HUB_TYPE_ENUM as any
                                                  )[
                                                      hub?.primaryOperation?.toUpperCase()
                                                  ]?.bgColor,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent:
                                                      'space-between',
                                                  padding: '6px 12px',
                                                  borderTopLeftRadius: 8,
                                                  borderTopRightRadius: 8,
                                                  border: 'solid 1px #f0e7e6',
                                                  borderBottom: 'none',
                                              }}
                                          >
                                              <Typography
                                                  variant="inherit"
                                                  style={{
                                                      color: '#252525',
                                                      fontSize: 14,
                                                  }}
                                              >
                                                  Hub Name:{' '}
                                                  <Typography
                                                      variant="inherit"
                                                      style={{
                                                          color: '#252525',
                                                          fontWeight: 600,
                                                          fontSize: 14,
                                                      }}
                                                  >
                                                      {hub?.hubName}
                                                  </Typography>
                                              </Typography>
                                          </Box>
                                          {hub?.shiftLineItems?.length &&
                                              hub?.shiftLineItems?.map(
                                                  (
                                                      shift: ShiftLineItem,
                                                      index: number,
                                                  ) => {
                                                      const border =
                                                          'solid 1px #efefef';
                                                      return (
                                                          <Box
                                                              key={`hub-shift-${shift.id}-${index}`}
                                                              style={{
                                                                  width: '100%',
                                                                  ...(index ===
                                                                  hub
                                                                      ?.shiftLineItems
                                                                      ?.length -
                                                                      1
                                                                      ? {
                                                                            borderBottomLeftRadius: 8,
                                                                            borderBottomRightRadius: 8,
                                                                            border: border,
                                                                            borderBottom:
                                                                                border,
                                                                            borderTop:
                                                                                'none',
                                                                        }
                                                                      : {
                                                                            borderLeft:
                                                                                border,
                                                                            borderRight:
                                                                                border,
                                                                        }),
                                                              }}
                                                          >
                                                              <div
                                                                  style={{
                                                                      width: '100%',
                                                                      ...(index ===
                                                                      hub
                                                                          ?.shiftLineItems
                                                                          ?.length -
                                                                          1
                                                                          ? {
                                                                                borderBottomLeftRadius:
                                                                                    '8px',
                                                                                borderBottomRightRadius:
                                                                                    '8px',
                                                                            }
                                                                          : {
                                                                                borderBottom:
                                                                                    '1px solid #efefef',
                                                                            }),
                                                                      padding:
                                                                          '6px 8px',
                                                                      display:
                                                                          'flex',
                                                                      justifyContent:
                                                                          'space-between',
                                                                      alignItems:
                                                                          'center',
                                                                  }}
                                                              >
                                                                  <div>
                                                                      <div
                                                                          style={{
                                                                              fontFamily:
                                                                                  'Nunito',
                                                                              fontSize:
                                                                                  '12px',
                                                                              fontWeight: 600,
                                                                              color: '#252525',
                                                                          }}
                                                                      >
                                                                          {shift?.name ??
                                                                              ''}
                                                                      </div>
                                                                      <div
                                                                          style={{
                                                                              fontFamily:
                                                                                  'Nunito',
                                                                              fontSize:
                                                                                  '10px',
                                                                              fontWeight: 400,
                                                                              color: '#252525',
                                                                          }}
                                                                      >
                                                                          {`${
                                                                              shift?.startTimeEpochMillis
                                                                                  ? moment(
                                                                                        shift?.startTimeEpochMillis,
                                                                                    ).format(
                                                                                        'HH:mm',
                                                                                    )
                                                                                  : ''
                                                                          } - ${
                                                                              shift?.endTimeEpochMillis
                                                                                  ? moment(
                                                                                        shift?.endTimeEpochMillis,
                                                                                    ).format(
                                                                                        'HH:mm',
                                                                                    )
                                                                                  : ''
                                                                          }`}
                                                                      </div>
                                                                  </div>
                                                                  {/* Actions in dropdown */}
                                                                  <Box
                                                                      style={{
                                                                          display:
                                                                              'flex',
                                                                          alignItems:
                                                                              'center',
                                                                      }}
                                                                  >
                                                                      {permissions?.update && (
                                                                          <IconButton
                                                                              size="small"
                                                                              onClick={(
                                                                                  e,
                                                                              ) =>
                                                                                  handleEditClick(
                                                                                      e,
                                                                                      shift,
                                                                                  )
                                                                              }
                                                                              disabled={
                                                                                  shift?.slotShift
                                                                              }
                                                                              style={{
                                                                                  padding: 4,
                                                                              }}
                                                                          >
                                                                              <EditIcon
                                                                                  style={{
                                                                                      fontSize: 14,
                                                                                      color: '#43419e',
                                                                                  }}
                                                                              />
                                                                          </IconButton>
                                                                      )}
                                                                      {permissions?.delete && (
                                                                          <IconButton
                                                                              size="small"
                                                                              onClick={(
                                                                                  e,
                                                                              ) =>
                                                                                  handleDeleteClick(
                                                                                      e,
                                                                                      shift,
                                                                                  )
                                                                              }
                                                                              style={{
                                                                                  padding: 4,
                                                                              }}
                                                                          >
                                                                              <DeleteIcon
                                                                                  style={{
                                                                                      fontSize: 14,
                                                                                      color: '#f44336',
                                                                                  }}
                                                                              />
                                                                          </IconButton>
                                                                      )}
                                                                  </Box>
                                                              </div>
                                                          </Box>
                                                      );
                                                  },
                                              )}
                                      </Box>
                                  );
                              },
                          },
                )}
                {...props}
            />

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <Modal
                    onClose={handleDeleteClose}
                    open={isDeleteModalOpen}
                    title="Delete Shift"
                    actions={deleteActions}
                    loading={deleteLoading}
                >
                    <Box>
                        <Typography
                            style={{
                                textAlign: 'center',
                            }}
                        >
                            Do you want to delete
                            <strong>{` ${selectedShift?.name} `}</strong>?
                        </Typography>
                        <Typography
                            style={{
                                textAlign: 'center',
                                marginTop: '8px',
                                marginBottom: '24px',
                            }}
                        >
                            You can't undo this action.
                        </Typography>
                    </Box>
                </Modal>
            )}

            {/* Edit Shift Modal */}
            {isEditModalOpen && selectedShift && (
                <AddShiftTimingModal
                    open={isEditModalOpen}
                    data={{
                        ...selectedShift,
                        hubId: hubInfo?.id,
                    }}
                    isEdit={true}
                    onClose={handleEditClose}
                />
            )}

            {/* Add Shift Modal */}
            {isAddModalOpen && (
                <AddShiftTimingModal
                    open={isAddModalOpen}
                    data={{
                        hubId: hubInfo?.id,
                        bluedartUniqueStoreId: hubInfo?.bluedartUniqueStoreId,
                    }}
                    isEdit={false}
                    onClose={handleAddClose}
                    isBlueDart={isBlueDart}
                />
            )}
        </>
    );
};

export default ShiftColumnItemPopperV2;
